import { AccountRegions } from '@payaca/types/accountTypes';
import type { CountryCode, PhoneNumber } from 'libphonenumber-js';
import { parsePhoneNumber } from 'libphonenumber-js';

export const formatPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return '';
  if (phoneNumber.length < 2) return phoneNumber;

  const firstCharacters = phoneNumber.substring(0, 2);
  if (firstCharacters === '44') {
    phoneNumber = '+' + phoneNumber;
  }
  try {
    const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
    if (parsedPhoneNumber) {
      return parsedPhoneNumber.formatInternational();
    }
  } catch (error) {
    return phoneNumber;
  }

  return phoneNumber;
};

export const getParsedPhoneNumber = (
  phoneNumber: string,
  defaultCountry?: CountryCode
): PhoneNumber | null => {
  if (!phoneNumber) return null;
  if (phoneNumber.length < 2) return null;

  const firstCharacters = phoneNumber.substring(0, 2);
  if (firstCharacters === '44') {
    phoneNumber = '+' + phoneNumber;
  }
  try {
    return parsePhoneNumber(phoneNumber, defaultCountry);
  } catch (error) {
    return null;
  }
};

/**
 * The E.164 form of a stored number for SMS delivery, interpreting a domestic
 * number in the account's region. Built on `normalisePhoneNumber` so the number
 * dialled is the same one the contact record indexes.
 *
 * Only a UK-style trunk `0` is ever stripped: a ten-digit US number keeps every
 * digit. (The previous implementation sliced the first character off anything
 * that did not already carry the calling code, which turned `8042485317` into
 * `+1042485317` and made Twilio reject every plain US number.)
 */
export const convertToInternationalPhoneNumber = (
  accountRegion: AccountRegions,
  phoneNumber: string
) => {
  const normalised = normalisePhoneNumber(phoneNumber, accountRegion);
  return normalised ? `+${normalised}` : phoneNumber;
};

const ACCOUNT_REGION_COUNTRY: Record<AccountRegions, CountryCode> = {
  [AccountRegions.AUSTRALIA]: 'AU',
  [AccountRegions.CANADA]: 'CA',
  [AccountRegions.IRELAND]: 'IE',
  [AccountRegions.NEW_ZEALAND]: 'NZ',
  [AccountRegions.SOUTH_AFRICA]: 'ZA',
  // libphonenumber uses the ISO country code GB, not the AccountRegions value 'uk'.
  [AccountRegions.UK]: 'GB',
  [AccountRegions.US]: 'US',
};

/**
 * Maps an account region to the libphonenumber country code used to parse and
 * dial its numbers. Returns `undefined` when no region is known, in which case
 * a domestic number cannot be assigned a calling code.
 */
export const accountRegionToCountryCode = (
  region: AccountRegions | null | undefined
): CountryCode | undefined =>
  region ? ACCOUNT_REGION_COUNTRY[region] : undefined;

/**
 * Whether the input parses to a valid phone number. The account region supplies
 * the default country so a domestic number (no `+`) can be validated; without a
 * region only an explicit international number can be judged. Never throws —
 * returns `false` for empty, malformed or unrecognised input.
 */
export const isValidPhoneNumber = (
  phoneNumber: string,
  region?: AccountRegions | null
): boolean =>
  getParsedPhoneNumber(
    phoneNumber,
    accountRegionToCountryCode(region)
  )?.isValid() ?? false;

// Resolved statically rather than via libphonenumber's getCountryCallingCode:
// that call loads the library's bundled metadata JSON, whose require breaks
// under loaders with ESM-interop JSON hooks (tsx wraps it as `{ default }`)
// and would make normalisation throw. Calling codes for these regions are
// stable, and this file's contract is to never throw.
const ACCOUNT_REGION_CALLING_CODE: Record<AccountRegions, string> = {
  [AccountRegions.AUSTRALIA]: '61',
  [AccountRegions.CANADA]: '1',
  [AccountRegions.IRELAND]: '353',
  [AccountRegions.NEW_ZEALAND]: '64',
  [AccountRegions.SOUTH_AFRICA]: '27',
  [AccountRegions.UK]: '44',
  [AccountRegions.US]: '1',
};

const MIN_PHONE_SEARCH_DIGITS = 5;

/**
 * Whether a search term has enough digits to be treated as a phone-number
 * search. Lets callers cheaply decide — before any region lookup that full
 * normalisation needs — whether the phone path is worth taking at all.
 */
export const looksLikePhoneSearch = (searchTerm: string): boolean =>
  searchTerm.replace(/\D/g, '').length >= MIN_PHONE_SEARCH_DIGITS;

/**
 * Reduces any telephone input to a normalised key of country calling code
 * followed by the national significant digits (E.164 without the leading `+`),
 * used identically for stored values and search queries so the two always agree.
 *
 * Rules, applied in order:
 *  1. A valid complete number → its E.164 digits.
 *  2. Explicit international form (leading `+` or `00`) → the digits, with the
 *     `+`/`00` dropped (the calling code is already present).
 *  3. Domestic form, when a region is known → strip a single leading trunk `0`
 *     and prepend the region's calling code.
 *  4. Otherwise (no region, no explicit calling code) → digits only.
 *
 * Returns `null` when the input has no digits. Never throws: malformed or abused
 * free-text input (embedded names, multiple numbers) falls through to a
 * best-effort key rather than raising.
 */
export const normalisePhoneNumber = (
  phoneNumber: string,
  region?: AccountRegions | null
): string | null => {
  if (!phoneNumber) return null;

  const country = accountRegionToCountryCode(region);

  // Rule 1: a number the library recognises as valid is authoritative.
  const parsed = getParsedPhoneNumber(phoneNumber, country);
  if (parsed?.isValid()) {
    return parsed.number.replace('+', '');
  }

  const digits = phoneNumber.replace(/\D/g, '');
  if (!digits) return null;

  // Rule 2: explicit international form already carries its calling code.
  if (phoneNumber.trim().startsWith('+')) {
    return digits;
  }
  if (digits.startsWith('00')) {
    return digits.slice(2) || null;
  }

  // Rule 3: a domestic number is interpreted in the account's region.
  if (region) {
    return `${ACCOUNT_REGION_CALLING_CODE[region]}${digits.replace(/^0/, '')}`;
  }

  // Rule 4: no country can be determined.
  return digits;
};

/**
 * Normalises a customer-search term for matching against stored numbers,
 * returning `null` when the term has too few digits to be a plausible phone
 * number so that ordinary name/word searches are not treated as phone searches.
 */
export const normalisePhoneSearch = (
  searchTerm: string,
  region?: AccountRegions | null
): string | null => {
  if (!looksLikePhoneSearch(searchTerm)) return null;
  return normalisePhoneNumber(searchTerm, region);
};
